import React, { ReactElement, useContext, useEffect, useState } from 'react'
import axios from 'axios'
import { Context, ProxyTransferFilter } from '@/components/Context'
import serverConfig from '@/server-config'
import { UpdateType } from '@/types/websocket'
import { ArtData } from '@/database/art'
import { PersonalWallet as Wallet } from '@/database/personalwallet'
import BaseWalletDetails from '@/components/wallets/BaseWalletDetails'
import CheckBox from '@/components/boxes/CheckBox'
import TransferToken from '@/components/wallets/TransferToken'
import TransferNft from '@/components/wallets/TransferNft'
import TransferNative from '@/components/wallets/TransferNative'
import { ArtState } from '@/database/types'
import ConnectWithNft from '@/components/wallets/ConnectWithNft'
import Link from 'next/link'
import { urlFromAddress } from '@/utils/shortenAddress'
import Box from '@/components/boxes/Box'
import { uni1d800 } from '@/utils/extras'
import Url from '@/components/boxes/Url'
import { GoLinkExternal } from 'react-icons/go'

axios.defaults.baseURL = serverConfig.baseURL

const portfolioDataOptions = (portfolioData: ArtData[], currentAccount: string | null) => {
    return portfolioData
        ?.filter((artData) => artData.tokenId && artData.art.title && artData.art.currentOwner === currentAccount)
        .filter(
            (artData) =>
                artData.art.state !== ArtState.FORCED_AUCTION &&
                artData.art.state !== ArtState.AUCTION &&
                artData.art.state !== ArtState.FOR_SALE &&
                artData.art.state !== ArtState.HIDDEN,
        )
        .map((artData) => (
            <option key={artData.tokenId} title={artData.art.title} value={artData.tokenId} className={'white'}>
                {artData.tokenId + ' - ' + artData.art.title}
            </option>
        ))
}

const WalletsOverview = (): ReactElement => {
    const { setActiveUpdates, filters, toggleFilter } = useContext(Context)
    const { currentAccount, portfolioData } = useContext(Context)
    const [personalWallet, setPersonalWallet] = useState<Wallet>(null as unknown as Wallet)
    const [tokenId, setTokenId] = useState('')
    const [showTooltip, setShowTooltip] = useState(false)

    const handleSelectNft = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedOptions = Array.from(event.target.selectedOptions)
        let found = false
        setTokenId('')
        for (const option of selectedOptions) {
            const selectedArt = portfolioData.find((art) => art.tokenId === parseInt(option.value))
            if (selectedArt?.nft && selectedArt.tokenId) {
                setPersonalWallet(selectedArt.nft.personalWallet)
                setTokenId(selectedArt.tokenId.toString())
                found = true
                break
            }
        }
        if (!found) {
            setPersonalWallet(null as unknown as Wallet)
            setTokenId('')
        }
    }

    useEffect(() => {
        setTokenId('')
        setPersonalWallet(null as unknown as Wallet)
    }, [currentAccount, portfolioData])

    useEffect(() => {
        setActiveUpdates([UpdateType.PORTFOLIO])
    }, [])

    return (
        <div>
            <h1>Your NFT Wallet</h1>
            <div className="header-container center bottom">
                <span className={'gray'}>Control Your Token Bound Accounts</span>
            </div>
            <div className="header-container center">
                <select className={'white'} required={true} multiple={false} onChange={handleSelectNft}>
                    <option className={'center'} key={'first'} title={'first'}>
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{' - Select your NFT -'}
                    </option>
                    {portfolioDataOptions(portfolioData, currentAccount)}
                </select>
            </div>
            {tokenId && (
                <>
                    <div className="bottom">
                        {personalWallet && (
                            <BaseWalletDetails wallet={personalWallet} title="PERSONAL WALLET" controller={``} />
                        )}
                    </div>
                    <div className="header-container center bottom">
                        <span className={'gray'}>Choose Transfer Type</span>
                    </div>
                    <p className="padding-bottom">
                        <CheckBox
                            btnName="Pulse"
                            classStyles=""
                            checked={filters.proxyTransfer === ProxyTransferFilter.NATIVE}
                            handleClick={() => toggleFilter('proxyTransfer', ProxyTransferFilter.NATIVE)}
                        />
                        <CheckBox
                            btnName="Token"
                            classStyles=""
                            checked={filters.proxyTransfer === ProxyTransferFilter.TOKEN}
                            handleClick={() => toggleFilter('proxyTransfer', ProxyTransferFilter.TOKEN)}
                        />
                        <CheckBox
                            btnName="NFT"
                            classStyles=""
                            checked={filters.proxyTransfer === ProxyTransferFilter.NFT}
                            handleClick={() => toggleFilter('proxyTransfer', ProxyTransferFilter.NFT)}
                        />
                    </p>
                    {filters.proxyTransfer === ProxyTransferFilter.NATIVE && <TransferNative wallet={personalWallet} />}
                    {filters.proxyTransfer === ProxyTransferFilter.TOKEN && <TransferToken wallet={personalWallet} />}
                    {filters.proxyTransfer === ProxyTransferFilter.NFT && <TransferNft wallet={personalWallet} />}
                    <ConnectWithNft tokenId={tokenId} wallet={personalWallet} />
                </>
            )}
        </div>
    )
}

export default WalletsOverview
